import type {
  ShapeBounds, ShadowBounds, ImageBounds, TitleBounds,
} from '@/modules/Node';
import { computeAllBounds, computeShapeCoordinates, computeTitleData } from '../services/NodeBoundsService';
import { drawShape, drawStroke } from '../services/NodeShapeRenderer';
import { drawText } from '../services/NodeTextRenderer';
import { NodeMediaRenderer } from '../services/NodeMediaRenderer';

// Re-export ParsedFrame type needed by Node.ts for the `animation` property passthrough
export type { NodeMediaRenderer } from '../services/NodeMediaRenderer';

export interface NodeTemplateConfig {
  shape: string;
  type: string;
  name: string;
  id: number | undefined;
  radius: number;
  width: number;
  height: number;
  rotate: number;
  border: boolean;
  borderColor: string;
  borderStyle: string;
  borderWidth: number;
  surface: boolean;
  bgColor: string;
  shadow: boolean;
  shadowColor: string;
  shadowX: number;
  shadowY: number;
  shadowBlur: number;
  title: string | null;
  titlePosition: string;
  textAlign: string;
  fontSize: number | string;
  fontColor: string | null;
  fontFamily: string;
  fontWeight: string;
  fontStyle: string;
  lineHeight: number;
  mediaCropEnabled: boolean;
  mediaSize: number;
  mediaOpacity: number;
  media: string | null;
  mediaURL: string | null;
  image: HTMLImageElement | HTMLCanvasElement | null;
  gifAnimation: boolean;
}

export default class NodeTemplate {
  shiftX: number | null;
  shiftY: number | null;

  id: number | undefined;
  type: string;
  name: string;
  occupied: boolean;
  snapshot: string | null;

  // design
  shape: string;
  radius: number;
  width: number;
  height: number;
  rotate: number;
  border: boolean;
  borderColor: string;
  borderStyle: string;
  borderWidth: number;
  surface: boolean;
  bgColor: string;

  shadow: boolean;
  shadowColor: string;
  shadowX: number;
  shadowY: number;
  shadowBlur: number;

  title: string | null;
  titlePosition: string;
  textAlign: string;
  fontSize: number;
  fontColor: string | null;
  fontFamily: string;
  fontWeight: string;
  fontStyle: string;
  lineHeight: number;

  media: string | null;
  mediaURL: string | null;
  image: HTMLImageElement | HTMLCanvasElement | null;
  _imageFetching: boolean;
  mediaCropEnabled: boolean;
  mediaSize: number;
  mediaOpacity: number;

  // node states && features
  globalAlpha: number;
  hover: boolean;
  selected: boolean;
  editingContent: boolean;
  editingAppearance: boolean;

  path: Path2D | null;

  ctx: CanvasRenderingContext2D | null;
  cacheInstance: HTMLCanvasElement | null;
  cacheQueue: Promise<void>;
  pixelRatio: number | null;

  // properties of objects which impact canvasCache dimensions
  shapeBounds: ShapeBounds | null;
  imageBounds: ImageBounds | null;
  shadowBounds: ShadowBounds | null;
  titleBounds: TitleBounds | null;

  // animation
  animation: boolean;
  gifAnimation: boolean;
  mediaRenderer: NodeMediaRenderer;

  // coords of the figure itself used to draw on the map
  xCache: number | null;
  yCache: number | null;

  ZOOM_MAX: number;

  constructor(
    {
      shape, type, name, id,
      radius, width, height, rotate,
      border, borderColor, borderStyle, borderWidth,
      surface, bgColor, shadow, shadowColor, shadowX, shadowY, shadowBlur,
      title, titlePosition, textAlign, fontSize, fontColor, fontFamily, fontWeight, fontStyle, lineHeight,
      mediaCropEnabled, mediaSize, mediaOpacity, media, mediaURL, image, gifAnimation
    }: NodeTemplateConfig)
  {
    this.shiftX = null;
    this.shiftY = null;

    this.id = id;
    this.type = type;
    this.name = name;
    this.occupied = false;
    this.snapshot = null; // image of the canvas

    // design
    this.shape = shape;
    this.radius = radius;
    this.width = width;
    this.height = height;
    this.rotate = rotate;
    this.border = border;
    this.borderColor = borderColor;
    this.borderStyle = borderStyle;
    this.borderWidth = borderWidth;
    this.surface = surface;
    this.bgColor = bgColor;

    this.shadow = shadow;
    this.shadowColor = shadowColor;
    this.shadowX = shadowX;
    this.shadowY = shadowY;
    this.shadowBlur = shadowBlur;

    this.title = title;
    this.titlePosition = titlePosition;
    this.textAlign = textAlign;
    this.fontSize = parseInt(fontSize as string);
    this.fontColor = fontColor;
    this.fontFamily = fontFamily;
    this.fontWeight = fontWeight;
    this.fontStyle = fontStyle;
    this.lineHeight = lineHeight;

    this.media = media;
    this.mediaURL = mediaURL;
    this.image = image;
    this._imageFetching = false;
    this.mediaCropEnabled = mediaCropEnabled;
    this.mediaSize = mediaSize;
    this.mediaOpacity = mediaOpacity;
    // -------

    // node states && features
    this.globalAlpha = 1;
    this.hover = false;
    this.selected = false;
    this.editingContent = false;
    this.editingAppearance = false;

    this.path = null;

    this.ctx = null;
    this.cacheInstance = null;
    this.cacheQueue = Promise.resolve();
    this.pixelRatio = null;

    // properties {width, height, x, y} of objects which impacts to canvasCache dimensions
    // to precisely detect canvas area
    this.shapeBounds = null;
    this.imageBounds = null;
    this.shadowBounds = null;
    this.titleBounds = null;

    // animation
    this.animation = false;
    this.gifAnimation = gifAnimation;
    this.mediaRenderer = new NodeMediaRenderer();

    // coords of the figure itself used to draw on the map
    this.xCache = null;
    this.yCache = null;

    this.ZOOM_MAX = 1; // 1.62854;
  }

  cacheInstanceSetDimensions(): void {
    if (!this.cacheInstance) {
      this.cacheInstance = document.createElement('canvas');
      this.ctx = this.cacheInstance.getContext('2d');
    }

    const { width, height } = this.getBounds();

    this.getPixelRatio();

    this.cacheInstance.width = Math.ceil(width * this.ZOOM_MAX * this.pixelRatio!);
    this.cacheInstance.height = Math.ceil(height * this.ZOOM_MAX * this.pixelRatio!);

    this.cacheInstance.style.width = `${width * this.ZOOM_MAX}px`;
    this.cacheInstance.style.height = `${height * this.ZOOM_MAX}px`;

    this.ctx!.scale(this.pixelRatio! * this.ZOOM_MAX, this.pixelRatio! * this.ZOOM_MAX);
  }

  async init(): Promise<void> {
    this.cacheInstanceSetDimensions();
    await this.cache();
    this.makeSnapshot();
  }

  async cache(): Promise<void> {
    const queueBefore = this.cacheQueue;
    this.cacheQueue = (async () => {
      try {
        // await previous calls resolve;
        await queueBefore;

        // run current cache()
        this.getPixelRatio();
        ({ x: this.xCache, y: this.yCache } = this._getShapeCoordinates());
        this._drawShape();

        await this._drawMedia();

        this._drawStroke();
        this._drawText();
      } catch (err) {
        console.error('An error on caching node instance', err);
        throw err;
      }
    })();

    return this.cacheQueue;
  }

  devDrawBorders(): void {
    const multiplier = this.ZOOM_MAX * this.pixelRatio!;
    this.ctx!.moveTo(0, 0);
    this.ctx!.lineTo(this.ctx!.canvas.width / multiplier, 0);
    this.ctx!.lineTo(this.ctx!.canvas.width / multiplier, this.ctx!.canvas.height / multiplier);
    this.ctx!.lineTo(0, this.ctx!.canvas.height / multiplier);
    this.ctx!.lineTo(0, 0);
    this.ctx!.lineWidth = 2;
    this.ctx!.strokeStyle = 'rgba(255,244,255,.6)';
    this.ctx!.stroke();
  }

  cacheClear(): void {
    this.ctx!.clearRect(0, 0, this.cacheInstance!.width, this.cacheInstance!.height);
  }

  makeSnapshot(canvas?: HTMLCanvasElement): void {
    if (!canvas) canvas = this.cacheInstance!;
    this.snapshot = this.toDataURL(canvas);
  }

  toDataURL(canvas?: HTMLCanvasElement): string {
    if (!canvas) canvas = this.cacheInstance!;
    return canvas.toDataURL();
  }

  getPixelRatio(): number {
    return this.pixelRatio = window.devicePixelRatio;
  }

  _drawStroke(): void {
    drawStroke(this.ctx!, this);
  }

  _drawShape(): void {
    this.path = drawShape(this.ctx!, this, this.pixelRatio!, this.ZOOM_MAX);
  }

  async _drawMedia(): Promise<void> {
    await this.mediaRenderer.drawMedia(this.ctx!, this);
    this.animation = this.mediaRenderer.animation;
  }

  _drawText(): void {
    drawText(this.ctx!, this);
  }

  getBounds(): { width: number; height: number } {
    const result = computeAllBounds(this.ctx!, this);
    this.shapeBounds = result.shapeBounds;
    this.shadowBounds = result.shadowBounds;
    this.imageBounds = result.imageBounds;
    this.titleBounds = result.titleBounds;
    return { width: result.canvasWidth, height: result.canvasHeight };
  }

  _getShapeCoordinates(): { x: number; y: number } {
    const result = computeShapeCoordinates(
      this.shapeBounds!, this.shadowBounds!, this.imageBounds!, this.titleBounds!,
    );
    this.shiftX = result.shiftX;
    this.shiftY = result.shiftY;
    return { x: result.x, y: result.y };
  }

}
